// couple of shared bits

import {
  has, get, set, some, keys, flow,
  hasIn, isNaN, negate, isNull, toPath,
  toPairs, isArray, toArray, toString, isObject,
  isString, isFinite, isBoolean, isInteger, isElement,
  flattenDeep, isUndefined, isObjectLike, isPlainObject, toLower as lodashToLower
} from 'lodash-es'

import debugFields from './debug-fields'

export {
  isRgb, toRgb,
  isHex, toHex
} from './colors'

const PIXEL = /^(-?\d+)px$/i
const PERCENT = /^(-?\d+(?:\.\d+)?)%$/

const POS_PIXEL = /^(\d+)px$/i
const POS_PERCENT = /^(\d+(?:\.\d+)?)%$/

// type check

export const isText = (v) => true
  && isString(v)
  && trim(v).length !== 0

export const isEmpty = (v) => false
  || isNull(v)
  || isUndefined(v)
  || isNaN(v)
  || (isString(v) && trim(v).length === 0)
export const isNotEmpty = negate(isEmpty)

export const isPixel = (v) => PIXEL.test(v)
export const isPercent = (v) => PERCENT.test(v)
export const isPosPixel = (v) => POS_PIXEL.test(v)
export const isPosPercent = (v) => POS_PERCENT.test(v)

export const isOpacity = (v) => isFinite(v) && v >= 0 && v <= 1
export const isPromise = (v) => !!v && hasIn(v, 'finally')
export const isEmitter = (v) => !!v
  && v.hasOwnProperty('has')
  && v.hasOwnProperty('on')
  && v.hasOwnProperty('off')
  && v.hasOwnProperty('pre')
  && v.hasOwnProperty('after')

export const isNgScope = (v) => !!v && v.hasOwnProperty('$root')
export const isNgElement = (v) => !!v && hasIn(v, 'prop') && hasIn(v, 'attr') && hasIn(v, 'find')

export const isElementLike = (v) => false
  || isElement(v)
  || isNgElement(v)

export const isNumberLike = (v) => false
  || isFinite(v)
  || isFinite(parseFloat(v))

export const isEmptyObject = (v) => isPlainObject(v) && keys(v).length === 0
export const isNotEmptyObject = (v) => isPlainObject(v) && keys(v).length !== 0

export const isEmptyArray = (v) => isArray(v) && v.length === 0
export const isNotEmptyArray = (v) => isArray(v) && v.length !== 0

const isCampaignKeyRE = /^[A-Za-z0-9]+$/

export const isCampaignKey = (v) => isCampaignKeyRE.test(v)

export const isPosNumber = (v) => true
  && isFinite(v)
  && v >= 0

export const isPosInteger = (v) => true
  && isInteger(v)
  && isPosNumber(v)

export const isPosOddInteger = (v) => true
  && isPosInteger(v)
  && ((v === 0) || (v % 2 === 1))

export const isGtZeroInteger = (v) => true
  && isPosInteger(v)
  && (v !== 0)

export const isLower = (v) => true
  && isText(v)
  && /^[^A-Z]+$/.test(v)

export const isList = (v) => true
  && isArray(v)
  && v.every(isText)

// type cast

export const toPosNumber = (v) => {
  if (!isNumberLike(v)) {
    return v
  }

  const num = parseFloat(v)
  const pos = num > 0 ? num : 0
  return pos
}

export const toInteger = (v) =>
  isNumberLike(v)
    ? Math.round(parseFloat(v))
    : v

export const toNumber = (v) =>
  isNumberLike(v)
    ? parseFloat(v)
    : v

const pixelSuffix = 'px'

export const toPixel = (v) =>
  isNumberLike(v)
    ? Math.round(parseFloat(v)) + pixelSuffix
    : v

export const toLower = (v) => {
  const text = toText(v)
  if (!isText(text)) {
    return text
  }

  return lodashToLower(text)
}

export const toOddInteger = (v) => {
  if (!isNumberLike(v)) {
    return v
  }

  const int = Math.round(parseFloat(v))
  if (int === 0) {
    return 0
  } else if (int > 0) {
    const evenInt = Math.floor(int / 2) * 2
    return evenInt + 1
  } else {
    const evenInt = Math.ceil(int / 2) * 2
    return evenInt - 1
  }
}

export const toGtZeroInteger = (v) => {
  if (!isNumberLike(v)) {
    return v
  }

  const int = Math.round(parseFloat(v))
  if (int <= 0) {
    return v
  } else {
    return int
  }
}

const booleanTrueFull = 'true'
const booleanTrueShort = 't'
const booleanFalseFull = 'false'
const booleanFalseShort = 'f'

export const toBoolean = (v) => {
  if (isBoolean(v)) {
    return v
  }

  if (v === booleanTrueFull || v === booleanTrueShort) {
    return true
  } else if (v === booleanFalseFull || v === booleanFalseShort) {
    return false
  } else {
    return v
  }
}

export const toText = (v) =>
  isString(v)
    ? trim(dropNbsp(v))
    : v

export const toOpacity = (v) => {
  if (!isNumberLike(v)) {
    return v
  }

  const pos = toPosNumber(v)
  const opacity = pos > 1 ? 1 : pos
  return opacity
}

export const toObject = (v) => {
  if (isObjectLike(v)) {
    return v
  } else if (isString(v)) {
    try {
      return JSON.parse(v)
    } catch (_err) {
      return v
    }
  } else {
    return v
  }
}

export const toRexExp = (v) =>
  isText(v)
    ? RegExp(v)
    : v

export const toList = (v) => {
  if (!isString(v)) {
    return v
  }

  const list = v.split(',').map(toText).filter(isText)
  return list
}

// flow of cast

export const toPosInteger = flow(toPosNumber, toInteger)
export const toPosOddInteger = flow(toPosNumber, toOddInteger)

const posPixelSuffix = 'px'

export const toPosPixel = (v) =>
  isNumberLike(v)
    ? toPosNumber(toInteger(v)) + posPixelSuffix
    : v

// hasUndefined

export const hasUndefined = (v) =>
  isObject(v)
    ? some(v, hasUndefined)
    : v === undefined
export const isAllDefined = negate(hasUndefined)

// falsy/truthy

export const isFalsy = (v) => !v
export const isTruthy = (v) => !!v

// safe versions of lodash helpers to work with StrictObj

export const safeGet = function(v, key, def) {
  if (has(v, key)) {
    return get(v, key, def)
  } else if (arguments.length === 3) {
    return def
  } else {
    return undefined
  }
}

export const safeSet = function(v, key, val) {
  const path = toPath(key)
  const last = path.pop()

  let curr = v
  for (const part of path) {
    if (hasIn(curr, part) && isObject(curr[part])) {
      curr = curr[part]
    } else {
      curr = curr[part] = /^\d$/.test(part) ? [] : {}
    }
  }

  curr[last] = val
  return v
}

export const safePick = function(v, ...input) {
  const toPick = flattenDeep(input)
  const toReturn = {}
  for (const key of toPick) {
    if (has(v, key)) set(toReturn, key, get(v, key))
  }
  return toReturn
}

export const safeAssign = function(v, ...input) {
  const toReturn = isObject(v) ? v : {}
  for (const obj of input) {
    for (const [objKey, objVal] of toPairs(obj)) {
      toReturn[objKey] = objVal
    }
  }
  return toReturn
}

export const safeConforms = function(source) {
  return debugFields(safeConforms_wrapper, {
    _source: source
  })

  function safeConforms_wrapper(object) {
    return safeConformsTo(object, source)
  }
}

export const safeConformsTo = function(object, source) {
  const sourceKeys = keys(source)
  for (let index  = 0, length = sourceKeys.length; index !== length; index++) {
    const sourceKey = sourceKeys[index]
    const sourceVal = source[sourceKey]
    const objectVal = safeGet(object, sourceKey, undefined)
    if (!sourceVal(objectVal)) return false
  }
  return true
}

// other

export const isZero = (v) => v === 0
export const zeroIfNaN = (v) => isNaN(v) ? 0 : v

const trimHasRE = /^\s|\s$/
const trimAllRE = /^\s+|\s+$/g
const trimReplacer = ''

export const trim = (str) => {
  if (!isString(str)) {
    return str
  }

  if (!trimHasRE.test(str)) {
    return str
  }

  return str.replace(trimAllRE, trimReplacer)
}

export const err2str = (err) => {
  if (isString(err)) return err
  else if (isObject(err) && isString(err.message)) return err.message
  else if (isObject(err)) return JSON.stringify(err)
  else return '' + err
}

export const args2str = (args) => {
  if (args.length === 0) return '<no-args>'
  else if (args.length === 1) return `[ ${toString(args[0])} ]`
  else return `[ length=${args.length}, ${ toArray(args).map(toString).join(', ') } ]`
}

export const numberWithSign = (v) => {
  if (!isNumberLike(v)) {
    return v
  }

  const num = parseFloat(v)
  const sign = num > 0 ? '+' : ''
  const sum = sign + num
  return sum
}

const dropNbspHasRE = /\xa0/
const dropNbspAllRE = /\xa0/g
const dropNbspReplacer = ' '

export const dropNbsp = (str) => {
  if (!isString(str)) {
    return str
  }

  if (!dropNbspHasRE.test(str)) {
    return str
  }

  return str.replace(dropNbspAllRE, dropNbspReplacer)
}

export const defaultTo = function(val, defaultVal) {
  if (arguments.length === 2) {
    return isEmpty(val) ? defaultVal : val
  } else {
    const length = arguments.length
    const limit = length - 1
    for (let index = 0; index !== limit; index++) {
      const next = arguments[index]
      if (!isEmpty(next)) return next
    }
    const last = arguments[limit]
    return last
  }
}

export const noargs = function(action) {
  return debugFields(noargs_wrapper, {
    _action: action
  })

  function noargs_wrapper() {
    return action()
  }
}

// tap

export const tap = function(action, config) {
  const beforeArray = safeGet(config, 'before')
  const afterArray = safeGet(config, 'after')

  return debugFields(tap_wrapper, {
    _action: action,
    _config: config
  })

  function tap_wrapper() {
    if (isArray(beforeArray)) {
      for (const before of beforeArray) {
        before(...arguments)
      }
    }

    const result = action(...arguments)

    if (isArray(afterArray)) {
      for (const after of afterArray) {
        after(...arguments, result)
      }
    }

    return result
  }
}

export const before = function(...args) {
  const action = args.pop()
  const before = flattenDeep(args)
  return tap(action, { before })
}

export const after = function(...args) {
  const action = args.shift()
  const after = flattenDeep(args)
  return tap(action, { after })
}

// callWith

export const callWith = function() {
  const args = arguments
  return debugFields(callWith_wrapper, {
    _args: args
  })

  function callWith_wrapper(action) {
    return action.apply(null, args)
  }
}

// eqWith

export const eqWith = function(val) {
  return debugFields(eqWith_wrapper, {
    _val: val
  })

  function eqWith_wrapper() {
    return eq(val, ...arguments)
  }
}

// logical operations

export const xor = function() {
  let count = 0
  for (let index = 0, length = arguments.length; index !== length; index++) {
    const val = arguments[index]
    if (val) {
      if (count === 0) {
        count++
      } else {
        count++
        break
      }
    }
  }

  return count === 1
}

export const and = function() {
  for (let index = 0, length = arguments.length; index !== length; index++) {
    const val = arguments[index]
    if (!val) return false
  }
  return true
}

export const or = function() {
  for (let index = 0, length = arguments.length; index !== length; index++) {
    const val = arguments[index]
    if (val) return true
  }
  return false
}

export const eq = function() {
  const first = arguments[0]

  for (let index = 1, length = arguments.length; index < length; index++) {
    const val = arguments[index]
    if (first !== val) return false
  }
  return true
}

export const makeXOR = function() {
  const actions = arguments
  return debugFields(makeXor_wrapper, {
    _actions: actions
  })

  function makeXor_wrapper() {
    let count = 0
    for (let index = 0, length = actions.length; index !== length; index++) {
      const action = actions[index]
      const val = action.apply(null, arguments)

      if (val) {
        if (count === 0) {
          count++
        } else {
          count++
          break
        }
      }
    }

    return count === 1
  }
}

export const makeOR = function() {
  const actions = arguments
  return debugFields(makeOr_wrapper, {
    _actions: actions
  })

  function makeOr_wrapper() {
    for (let index = 0, length = actions.length; index !== length; index++) {
      const action = actions[index]
      const val = action.apply(null, arguments)
      if (val) return true
    }
    return false
  }
}

export const makeAND = function() {
  const actions = arguments
  return debugFields(makeAnd_wrapper, {
    _actions: actions
  })

  function makeAnd_wrapper() {
    for (let index = 0, length = actions.length; index !== length; index++) {
      const action = actions[index]
      const val = action.apply(null, arguments)
      if (!val) return false
    }
    return true
  }
}

export const makeEQ = function() {
  const actions = arguments
  return debugFields(makeEq_wrapper, {
    _actions: actions
  })

  function makeEq_wrapper() {
    const first = actions[0].apply(null, arguments)

    for (let index = 1, length = actions.length; index < length; index++) {
      const action = actions[index]
      const val = action.apply(null, arguments)
      if (first !== val) return false
    }
    return true
  }
}

// bindings

const oneTimeBindingHasRE = /^::/
const oneTimeBindingFullRE = /^::\s*/
const oneTimeBindingReplacer = ''

export const isOneTimeBinding = (raw) => {
  const val = toText(raw)
  return true
    && isText(val)
    && oneTimeBindingHasRE.test(val)
}

export const isOneTimeOptionalBinding = (raw) => {
  const val = toText(raw)
  return false
    || isEmpty(val)
    || isOneTimeBinding(val)
}

export const isCommonBinding = (raw) => {
  const val = toText(raw)
  return true
    && isText(val)
    && !oneTimeBindingHasRE.test(val)
}

export const isCommonOptionalBinding = (raw) => {
  const val = toText(raw)
  return false
    || isEmpty(val)
    || isCommonBinding(val)
}

export const toCommonBinding = (raw) => {
  const val = toText(raw)
  if (isCommonBinding(val)) {
    return val
  } else if (isOneTimeBinding(val)) {
    return val.replace(oneTimeBindingFullRE, oneTimeBindingReplacer)
  } else {
    return val
  }
}
